import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRightIcon, BookmarkIcon, CheckIcon, ClockIcon, DollarSignIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type PipelineItem = {
  id: string;
  name: string;
  value: number;
  probability: number;
  stage: "negotiation" | "proposal" | "contract";
  expected_close: string;
  type: "pipeline" | "backlog";
  priority: "high" | "medium" | "low";
};

const StageIndicator = ({ stage }: { stage: PipelineItem["stage"] }) => {
  const stages = ["negotiation", "proposal", "contract"];
  const currentIndex = stages.indexOf(stage);
  
  return (
    <div className="flex items-center gap-2">
      {stages.map((s, index) => (
        <div key={s} className="flex items-center">
          <div className={`h-3 w-3 rounded-full ${
            index <= currentIndex ? "bg-primary" : "bg-muted"
          }`} />
          {index < stages.length - 1 && (
            <div className={`w-8 h-0.5 ${
              index < currentIndex ? "bg-primary" : "bg-muted"
            }`} />
          )}
        </div>
      ))}
    </div>
  );
};

export const PipelineVisual = () => {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['pipeline-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_items')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as PipelineItem[];
    },
  });

  const pipelineData = items.filter(item => item.type === 'pipeline');
  const backlogData = items.filter(item => item.type === 'backlog');

  const totalPipelineValue = pipelineData.reduce((acc, item) => acc + (item.value * (item.probability || 0) / 100), 0);
  const totalBacklogValue = backlogData.reduce((acc, item) => acc + item.value, 0);

  const getPriorityColor = (priority: PipelineItem["priority"]) => {
    switch (priority) {
      case "high":
        return "bg-primary/20 text-primary";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Pipeline Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-primary">Active Pipeline</CardTitle>
            <Badge variant="outline" className="text-primary">
              ${(totalPipelineValue / 1000000).toFixed(1)}M
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {pipelineData.map((item) => (
              <div key={item.id} className="relative space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{item.name}</h3>
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{item.expected_close}</span>
                  </div>
                </div>
                <StageIndicator stage={item.stage} />
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <DollarSignIcon className="h-4 w-4 text-primary" />
                    <span>${(item.value / 1000000).toFixed(1)}M</span>
                  </div>
                  <Badge variant="secondary">
                    {item.probability}% probability
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Backlog Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-primary">Opportunity Backlog</CardTitle>
            <Badge variant="outline" className="text-primary">
              ${(totalBacklogValue / 1000000).toFixed(1)}M
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {backlogData.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 rounded-lg bg-accent"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <BookmarkIcon className="h-4 w-4 text-primary" />
                    <h3 className="font-medium">{item.name}</h3>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <DollarSignIcon className="h-4 w-4" />
                    <span>${(item.value / 1000000).toFixed(1)}M</span>
                  </div>
                </div>
                <Badge className={getPriorityColor(item.priority)}>
                  {item.priority} priority
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};